<template>
  <div class="flex gap-2 pt-4 pb-8">
    <div>
      <DropDownSelect>
        <template #activator="{ open, showDropDown }">
          <button
            class="flex items-center gap-1 rounded-sm border border-solid p-2 text-body-s"
            :class="timeframe !== TimeFrameOptionsEnum.NONE || showDropDown ? 'filter-selected' : 'filter-default'"
            data-testid="agency-reviews-filter-timeframe-button"
            @click.prevent="open"
          >
            {{ t('Reviews.agency.reviews.filters.timeframe.filtername') }}
            <ChevronDownIcon class="lucide-icon-m" />
          </button>
        </template>
        <template #default="{ close }">
          <ul class="options-list m-0 w-80 list-none p-0">
            <div class="flex items-center justify-between bg-surface-elevation-4 px-3 py-2 txt-body-s-bold">
              {{ t('Reviews.agency.reviews.filters.timeframe.headline') }}
              <XIcon class="lucide-icon-s cursor-pointer" @click.prevent="close" />
            </div>
            <li v-for="option in timeframeOptions" :key="option.value">
              <label class="peer block cursor-pointer">
                <input
                  type="radio"
                  :value="option.value"
                  :checked="timeframe === option.value"
                  name="agency-timeframe-option"
                  class="peer pointer-events-none absolute hidden"
                  @change="onTimeframeChange(option.value)"
                />
                <span class="block px-3 py-2 peer-checked:hidden hover:bg-surface-elevation-2">
                  {{ option.text }}
                </span>
                <span class="hidden items-center px-3 py-2 peer-checked:flex hover:bg-surface-elevation-2">
                  <span class="text-selected">{{ option.text }}</span>
                  <CheckIcon class="ml-4 lucide-icon-s" />
                </span>
              </label>
            </li>
          </ul>
        </template>
      </DropDownSelect>
    </div>
    <div>
      <ReviewsFilterMultiSelect
        :model-value="ratings"
        :options="ratingOptions"
        :label="t('Reviews.agency.reviews.filters.rating.filtername')"
        data-testid="agency-reviews-filter-rating"
        @update:model-value="onRatingsChange"
      >
        <template #header="{ close }">
          <div class="flex w-auto items-center justify-between bg-surface-elevation-4 px-2 py-2 txt-body-s-bold">
            {{ t('Reviews.agency.reviews.filters.rating.headline') }}
            <XIcon class="lucide-icon-s cursor-pointer" @click.prevent="close" />
          </div>
        </template>
      </ReviewsFilterMultiSelect>
    </div>
    <div>
      <ReviewsFilterMultiSelect
        :model-value="states"
        :options="stateOptions"
        :label="t('Reviews.agency.reviews.filters.state.filtername')"
        data-testid="agency-reviews-filter-state"
        @update:model-value="onStateChange"
      >
        <template #header="{ close }">
          <div class="flex w-48 items-center justify-between bg-surface-elevation-4 px-2 py-2 txt-body-s-bold">
            {{ t('Reviews.agency.reviews.filters.state.headline') }}
            <XIcon class="lucide-icon-s cursor-pointer" @click.prevent="close" />
          </div>
        </template>
      </ReviewsFilterMultiSelect>
    </div>
    <button
      class="txt-body-s-bold"
      :class="hasActiveFilters ? '' : 'hidden'"
      :disabled="!hasActiveFilters"
      data-testid="agency-reviews-filter-clear"
      @click="onReset"
    >
      <span class="inline-flex items-center gap-1">
        <XIcon class="lucide-icon-s" />
        {{ t('Reviews.agency.reviews.filters.clearAllButton') }}
      </span>
    </button>
  </div>
</template>

<script setup lang="ts">
import { ChevronDownIcon, XIcon, CheckIcon } from '@ramp106/omrjs-core-ui/icons'
import { track } from '@ramp106/omrjs-tracking-reviews'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import ReviewsFilterMultiSelect from '@/components/ReviewsManagement/Reviews/ReviewsFilterMultiSelect.vue'
import { TimeFrameOptionsEnum, TIMEFRAME_FILTER_GTM_VALUES } from '@/components/ReviewsManagement/Reviews/types'
import { RecommendationRatingKeyEnum, UniversalReviewStates } from '@/gql/reviews'

import { universalReviewStatusColor, universalReviewStatusFilterOrder, universalReviewStatusLabelKey } from './universalReviewStatus'
import { useRouterQueryAgencyReviewsFilters } from './useRouterQueryAgencyReviewsFilters'

const FILTER_GROUP = 'manage_service_reviews_all'

const { t } = useI18n()
const { timeframe, ratings, states, hasActiveFilters, setTimeframe, setRatings, setStates, resetFilters } =
  useRouterQueryAgencyReviewsFilters()

const timeframeOptions = computed(() => [
  { text: t('Reviews.agency.reviews.filters.timeframeOptions.all'), value: TimeFrameOptionsEnum.NONE },
  { text: t('Reviews.agency.reviews.filters.timeframeOptions.optionOne'), value: TimeFrameOptionsEnum.LAST_30_DAYS },
  { text: t('Reviews.agency.reviews.filters.timeframeOptions.optionTwo'), value: TimeFrameOptionsEnum.LAST_3_MONTH },
  { text: t('Reviews.agency.reviews.filters.timeframeOptions.optionThree'), value: TimeFrameOptionsEnum.LAST_6_MONTH },
  { text: t('Reviews.agency.reviews.filters.timeframeOptions.optionFour'), value: TimeFrameOptionsEnum.LAST_12_MONTH },
  { text: t('Reviews.agency.reviews.filters.timeframeOptions.optionFive'), value: TimeFrameOptionsEnum.OLDER_THAN_12_MONTH },
])

const ratingOptions = [
  { text: '1.0', value: RecommendationRatingKeyEnum.Bad, renderType: 'ratingStars' },
  { text: '2.0', value: RecommendationRatingKeyEnum.Worse, renderType: 'ratingStars' },
  { text: '3.0', value: RecommendationRatingKeyEnum.Good, renderType: 'ratingStars' },
  { text: '4.0', value: RecommendationRatingKeyEnum.Better, renderType: 'ratingStars' },
  { text: '5.0', value: RecommendationRatingKeyEnum.Best, renderType: 'ratingStars' },
]

const stateOptions = computed(() =>
  universalReviewStatusFilterOrder.map((value) => ({
    text: t(`Reviews.agency.reviews.table.statusLabel.${universalReviewStatusLabelKey(value)}`),
    value,
    renderType: 'stateBadges',
    color: universalReviewStatusColor(value),
  })),
)

function onTimeframeChange(value: TimeFrameOptionsEnum) {
  track('filter_apply', {
    surface: 'filter',
    filter_group: FILTER_GROUP,
    filter_type: 'dropdown',
    filter_name: 'timeframe',
    filter_value: TIMEFRAME_FILTER_GTM_VALUES[value],
  })
  setTimeframe(value)
}

function onRatingsChange(value: string[]) {
  track('filter_apply', {
    surface: 'filter',
    filter_group: FILTER_GROUP,
    filter_type: 'tickbox',
    filter_name: 'rating',
    filter_value: value.join(','),
  })
  setRatings(value as RecommendationRatingKeyEnum[])
}

function onStateChange(value: string[]) {
  track('filter_apply', {
    surface: 'filter',
    filter_group: FILTER_GROUP,
    filter_type: 'tickbox',
    filter_name: 'state',
    filter_value: value.join(','),
  })
  setStates(value as UniversalReviewStates[])
}

function onReset() {
  track('filter_apply', {
    surface: 'filter',
    filter_group: FILTER_GROUP,
    filter_type: 'button',
    filter_name: 'reset_filter',
    filter_value: '',
  })
  resetFilters()
}
</script>

<style scoped>
@reference '@ramp106/omrjs-config-tailwind/styles/index.css';

.filter-selected {
  @apply border-strong text-subtle;
}
.filter-default {
  @apply border-main text-subtlest disabled:border-disabled disabled:text-disabled;
}
</style>
